import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { applyHeadlineToUnitPrice } from '@supply-mover-2-0/core';
import {
	ImagePlaceholder,
	PromoChip,
	ResilientImage,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AddToCartControl } from '../components/AddToCartControl';
import { FavoriteButton } from '../components/FavoriteButton';
import { ItemDesignationBadges } from '../components/ItemDesignationBadges';
import { ProductIdentifierLines } from '../components/ProductIdentifierLines';
import { StockQuantityText } from '../components/StockQuantityText';
import { VariantPicker } from '../components/VariantPicker';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { CATALOG_ITEM_QUERY_KEY } from '../lib/catalogQueryKeys';
import { formatCurrency } from '../lib/formatCurrency';
import { formatStatementDate } from '../lib/formatStatementDate';
import { resolveStockQuantityLine } from '../lib/stockQuantityLine';
import * as catalogService from '../services/portalCatalogService';

/**
 * Product detail page -- mobile-first layout with full-width image,
 * product info, quantity selector, add-to-cart, and favorite toggle.
 * Wired to real API via portalCatalogService + portalCartService.
 */
export function ProductDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { token, isAuthenticated } = usePortalAuth();
	const display = useDisplaySettings();
	const queryClient = useQueryClient();

	const [selectedImageIndex, setSelectedImageIndex] = useState(0);

	const itemId = id != null ? parseInt(id, 10) : NaN;

	// Fetch product detail. Allowed for anonymous visitors when the
	// storefront permits anonymous browsing.
	const { data: productData, isLoading } = useQuery({
		queryKey: [...CATALOG_ITEM_QUERY_KEY, itemId, display.isAnonymousVisitor],
		queryFn: () => catalogService.getCatalogItem(token ?? '', itemId),
		enabled: (token != null || display.isAnonymousVisitor) && !isNaN(itemId),
	});

	const product = productData?.data ?? null;
	const variantPicker = productData?.variantPicker ?? null;

	// Build images array: primary image first, then any additional images
	const allImages =
		product != null
			? [
					...(product.primaryImageUrl != null ? [product.primaryImageUrl] : []),
					...(product.images ?? [])
						.filter((img) => img.imageUrl !== product.primaryImageUrl)
						.map((img) => img.imageUrl),
				]
			: [];

	const currentImage =
		allImages.length > 0
			? (allImages[selectedImageIndex] ?? allImages[0])
			: null;

	// --- Loading skeleton ---
	if (isLoading) {
		return (
			<div className="max-w-4xl mx-auto animate-pulse">
				<div className="px-4 py-3">
					<div className="h-5 w-16 bg-brand-input rounded" />
				</div>
				<div className="md:flex md:gap-8 md:px-4">
					{display.showItemImages && (
						<div className="md:w-1/2">
							<div className="aspect-square bg-brand-input md:rounded-xl" />
						</div>
					)}
					<div className="flex-1 px-4 py-4 md:px-0 space-y-4">
						<div className="h-7 bg-brand-input rounded w-3/4" />
						<div className="h-4 bg-brand-input rounded w-1/2" />
						<div className="h-4 bg-brand-input rounded w-1/3" />
						<div className="h-8 bg-brand-input rounded w-1/4 mt-4" />
						<div className="h-12 bg-brand-input rounded mt-6" />
					</div>
				</div>
			</div>
		);
	}

	// --- Product not found ---
	if (product == null) {
		return (
			<div className="max-w-4xl mx-auto px-4 py-12 text-center">
				<p className="text-brand-muted mb-4">{t('Product not found.')}</p>
				<button
					type="button"
					onClick={() => navigate('/shop')}
					className="text-sm text-portal-accent hover:underline"
				>
					{t('Back to catalog')}
				</button>
			</div>
		);
	}

	// PD-16750: the exact number, or the capped, range or level display the
	// server sent in its place.
	const stockLine = display.canSeeStockQuantity
		? resolveStockQuantityLine(product, display.stockDisplayMode)
		: null;

	return (
		<div className="max-w-4xl mx-auto">
			{/* Back button */}
			<div className="px-4 py-3">
				<button
					type="button"
					onClick={() => navigate(-1)}
					className="flex items-center gap-2 text-sm text-brand-muted hover:text-default min-h-touch"
				>
					<FontAwesomeIcon icon={faArrowLeft} />
					<span>{t('Back')}</span>
				</button>
			</div>

			<div className="md:flex md:gap-8 md:px-4">
				{/* Product image(s). PD-17144: when the brand hides item images
				    the whole column goes, main picture and gallery alike, and
				    the info column below (flex-1) takes the full width. */}
				{display.showItemImages && (
					<div className="md:w-1/2">
						{/* Main image — wider-than-tall on mobile (3:2) so the price,
						    stock pill, add-to-cart, and description start above the
						    fold. Square on desktop where the image lives in a half-
						    width column and the surrounding info has its own column
						    of breathing room. */}
						<div className="aspect-[3/2] md:aspect-square bg-brand-input overflow-hidden md:rounded-xl">
							<ResilientImage
								src={currentImage}
								alt={product.description ?? ''}
								className="w-full h-full object-contain"
								onLikelyStaleUrl={() => {
									void queryClient.invalidateQueries({
										queryKey: [...CATALOG_ITEM_QUERY_KEY, itemId],
									});
								}}
								fallback={<ImagePlaceholder size="lg" />}
							/>
						</div>

						{/* Image gallery thumbnails */}
						{allImages.length > 1 && (
							<div className="flex gap-2 px-4 py-3 md:px-0 overflow-x-auto">
								{allImages.map((imgUrl, idx) => (
									<button
										key={idx}
										type="button"
										onClick={() => setSelectedImageIndex(idx)}
										className={`w-16 h-16 shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${
											idx === selectedImageIndex
												? 'border-portal-accent'
												: 'border-brand-border hover:border-strong'
										}`}
									>
										<ResilientImage
											src={imgUrl}
											alt={`${t(product.description ?? '')} ${t(String(idx + 1))}`}
											className="w-full h-full object-cover"
											loading="lazy"
											onLikelyStaleUrl={() => {
												void queryClient.invalidateQueries({
													queryKey: [...CATALOG_ITEM_QUERY_KEY, itemId],
												});
											}}
											fallback={null}
										/>
									</button>
								))}
							</div>
						)}
					</div>
				)}

				{/* Product info */}
				<div className="flex-1 px-4 py-4 md:px-0">
					<h1 className="text-xl md:text-2xl font-bold text-default mb-2">
						{t(product.description ?? '')}
					</h1>

					{product.manufacturer != null && (
						<p className="text-sm text-brand-muted mb-1">
							{t('Manufacturer')}: {t(product.manufacturer)}
							{product.manufacturerItemId != null && (
								<span className="ml-2 text-faint">
									({t(product.manufacturerItemId)})
								</span>
							)}
						</p>
					)}

					{product.category != null && (
						<p className="text-sm text-brand-muted mb-1">
							{t('Category')}: {t(product.category)}
							{product.subcategory != null && (
								<span> / {t(product.subcategory)}</span>
							)}
						</p>
					)}

					{product.unitOfMeasure != null && (
						<p className="text-sm text-brand-muted mb-1">
							{t('UOM')}: {t(product.unitOfMeasure)}
						</p>
					)}

					{/* PD-16126: opt-in NDC / Size lines, styled like the fact
					    lines above (the item number is in Product Details). */}
					<ProductIdentifierLines
						sku={product.externalId}
						ndc={product.ndc}
						size={product.size}
						display={display}
						variant="detail"
					/>

					{product.designations != null && (
						<div className="mb-4 mt-2">
							<ItemDesignationBadges designations={product.designations} />
						</div>
					)}

					{variantPicker != null && (
						<VariantPicker
							picker={variantPicker}
							onSelect={(variantItemId) => {
								setSelectedImageIndex(0);
								void navigate(`/product/${variantItemId}`, { replace: true });
							}}
						/>
					)}

					{/*
					 * Price section.
					 *
					 * Pricing model (per product owner — see notes in
					 * `useDisplaySettings` and the renderer rewrite):
					 *
					 *   • `contractPrice` is NOT a discount — it's how this
					 *     customer's price is *looked up*. When present, it
					 *     supersedes list price as the base. We do NOT
					 *     strikethrough list when a contract exists, and we
					 *     do NOT show a "Contract Price" label (the green
					 *     label implied savings; contract pricing can be
					 *     above list and frequently is).
					 *   • Promos apply on top of whatever base the lookup
					 *     produced. When a promo applies, the strikethrough
					 *     is `basePrice` (contract or list — whichever is
					 *     in effect) and the bold final is the post-promo
					 *     price.
					 *   • The promo chip names the campaign. For now we
					 *     read `product.promoHeadline` (advertising-rank
					 *     pick); the next pass aligns this with the
					 *     engine-applied promo so detail/cart/checkout
					 *     all name the same campaign.
					 *
					 * States in priority order:
					 *   1. Pricing hidden (canSeeCatalogPricing=false) →
					 *      render nothing (the whole block is gated below).
					 *   2. No price at all → "Call for price".
					 *   3. Promo applies AND yields a lower price →
					 *      strikethrough basePrice + bold finalPrice +
					 *      <PromoChip>.
					 *   4. Otherwise → bold basePrice (clean, no chrome).
					 *
					 * PD-14239 / PD-14775: the ENTIRE price block is hidden
					 * when catalog pricing is off for this visitor — the
					 * active client's per-user hidePricing flag or the
					 * site-wide hideCatalogPricing setting
					 * (canSeeCatalogPricing=false). The server nulls the
					 * price fields too; gating the block removes the element
					 * so no empty price slot remains. This also covers the
					 * anonymous pricing-hidden storefront.
					 */}
					{display.canSeeCatalogPricing && (
						<div className="mb-6">
							{(() => {
								const listPriceNum =
									product.price != null ? parseFloat(product.price) : NaN;
								const contractPriceNum =
									product.contractPrice != null
										? parseFloat(product.contractPrice)
										: NaN;
								// `contractPrice ?? listPrice` — but as numbers,
								// since `??` on the string forms loses us the
								// ability to compare against the promo result.
								const basePrice = Number.isFinite(contractPriceNum)
									? contractPriceNum
									: Number.isFinite(listPriceNum)
										? listPriceNum
										: NaN;

								if (!Number.isFinite(basePrice)) {
									return (
										<span className="text-base text-faint">
											{t('Call for price')}
										</span>
									);
								}

								const promoFinalPrice =
									product.promoHeadline != null
										? applyHeadlineToUnitPrice(basePrice, product.promoHeadline)
										: null;

								const promoActive =
									promoFinalPrice != null && promoFinalPrice < basePrice;

								if (promoActive && product.promoHeadline != null) {
									// Reading order: campaign attribution → numeric
									// price. The earlier order (price first, chip
									// below) read as "$10.46 — and oh by the way
									// it's on sale"; lifting the chip above the
									// price line makes the campaign the cause and
									// the price the consequence.
									return (
										<div>
											<div className="mb-2">
												<PromoChip
													variant="accent"
													name={t(
														product.promoHeadline.promoName ??
															product.promoHeadline.headline ??
															'',
													)}
													badge={
														product.promoHeadline.badgeText != null
															? t(product.promoHeadline.badgeText)
															: null
													}
												/>
											</div>
											<div>
												<span className="text-sm text-faint line-through mr-2">
													{t(formatCurrency(basePrice) ?? '')}
												</span>
												<span className="text-2xl font-bold text-default">
													{t(formatCurrency(promoFinalPrice) ?? '')}
												</span>
											</div>
										</div>
									);
								}

								return (
									<span className="text-2xl font-bold text-default">
										{t(formatCurrency(basePrice) ?? '')}
									</span>
								);
							})()}
						</div>
					)}

					{/* PD-13692: quantity on hand near the price, gated on the
					    storefront's stock-quantity visibility preference.
					    PD-16750: exact, capped, range or level wording. */}
					{stockLine != null && (
						<p className="text-sm text-brand-muted mb-4">
							<StockQuantityText line={stockLine} />
						</p>
					)}

					{/* Add to Cart + Favorite. Favorite is auth-only — anon
					    visitors can't save items to a list without an account,
					    and rendering a tap-target that pops a sign-in wall on
					    every interaction is worse UX than just hiding it. */}
					<div className="flex items-start gap-3 mb-6">
						<div className="flex-1">
							<AddToCartControl
								itemId={itemId}
								variant="full"
								inStock={product.inStock}
								deaSchedule={product.designations.deaSchedule}
								isPriced={product.isPriced}
							/>
						</div>
						{/* Favorites is a catalog-browse feature (gated nav + API),
						    so hide the toggle on account-portal-only storefronts
						    where the product is reached only from an order/invoice. */}
						{isAuthenticated && display.canBrowseCatalog && (
							<FavoriteButton
								itemId={itemId}
								isFavorite={product.isFavorite === true}
								size="md"
							/>
						)}
					</div>

					{/* Extended description */}
					{product.extendedDescription != null && (
						<div className="mt-8 border-t border-brand-border pt-6">
							<h2 className="text-sm font-semibold text-default mb-2">
								{t('Description')}
							</h2>
							<p className="text-sm text-brand-muted whitespace-pre-line">
								{t(product.extendedDescription)}
							</p>
						</div>
					)}

					{/* Product details table */}
					<div className="mt-6 border-t border-brand-border pt-6">
						<h2 className="text-sm font-semibold text-default mb-3">
							{t('Product Details')}
						</h2>
						<dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
							{product.externalId != null && (
								<>
									<dt className="text-brand-muted">{t('Item #')}</dt>
									<dd className="text-default">{t(product.externalId)}</dd>
								</>
							)}
							{display.showItemNdc &&
								product.ndc != null &&
								product.ndc.trim() !== '' && (
									<>
										<dt className="text-brand-muted">{t('NDC')}</dt>
										<dd className="text-default">{t(product.ndc)}</dd>
									</>
								)}
							{product.manufacturer != null && (
								<>
									<dt className="text-brand-muted">{t('Manufacturer')}</dt>
									<dd className="text-default">{t(product.manufacturer)}</dd>
								</>
							)}
							{product.manufacturerItemId != null && (
								<>
									<dt className="text-brand-muted">{t('Mfr Item #')}</dt>
									<dd className="text-default">
										{t(product.manufacturerItemId)}
									</dd>
								</>
							)}
							{product.category != null && (
								<>
									<dt className="text-brand-muted">{t('Category')}</dt>
									<dd className="text-default">{t(product.category)}</dd>
								</>
							)}
							{product.unitOfMeasure != null && (
								<>
									<dt className="text-brand-muted">{t('Unit of Measure')}</dt>
									<dd className="text-default">{t(product.unitOfMeasure)}</dd>
								</>
							)}
							{display.showItemSize &&
								product.size != null &&
								product.size.trim() !== '' && (
									<>
										<dt className="text-brand-muted">{t('Size')}</dt>
										<dd className="text-default">{t(product.size)}</dd>
									</>
								)}
							{/* PD-13692: soonest lot expiration (lot-controlled items) */}
							{product.expirationDate != null && (
								<>
									<dt className="text-brand-muted">
										{t('Soonest Expiration')}
									</dt>
									<dd className="text-warning">
										{t(formatStatementDate(product.expirationDate))}
									</dd>
								</>
							)}
						</dl>
					</div>
				</div>
			</div>
		</div>
	);
}
